// modules
import dayjs from 'dayjs'
import { get } from 'lodash'

import httpClient, { catchError } from '@emmacare/common/api/httpClient'

// store
import {
  setPatientActiveProgram,
  setPatientActivityLog,
  setPatientAllPrograms,
  setPatientAssignedMeasurement,
  setPatientGoalInterventions,
  setPatientMeasurementActivityLog,
  setPatientProfile,
  setPatientVitalGraph,
  setPatienttGoals
} from 'src/store/modules/patient'

// constants
import {
  DATE_TIME_FORMAT_FOR_SERVER,
  PATIENT_EXTERNAL_DOCUMENT_GET_PATCH_DELETE,
  PATIENT_GET_ACTIVE_PROGRAM,
  PATIENT_GET_ACTIVITY_LOG,
  PATIENT_GET_ACTIVITY_LOG_EXPORT,
  PATIENT_GET_ACTIVITY_LOG_EXPORT_PDF,
  PATIENT_GET_ALL_PROGRAMS,
  PATIENT_GET_ASSIGNED_MEASUREMENT,
  PATIENT_GET_CARE_PLAN_REPORT,
  PATIENT_GET_GOALS,
  PATIENT_GET_GOAL_INTERVENTIONS,
  PATIENT_GET_PROFILE,
  PATIENT_GET_VITAL_GRAPH
} from 'src/utils/constants'
import exportReport from 'src/utils/functions/exportReport'

export const fetchPatient =
  (config: any = {}) =>
  async (dispatch: any) => {
    const { params = {}, query = {} } = config

    try {
      const profile = await httpClient.request({
        method: 'get',
        url: {
          pathname: PATIENT_GET_PROFILE,
          params,
          query
        }
      })

      dispatch(setPatientProfile(profile))

      return true
    } catch (_) {
      return false
    }
  }

export const fetchPatientActiveProgram =
  (config: any) => async (dispatch: any) => {
    const { params = {} } = config

    try {
      const activeProgram = await httpClient.request({
        method: 'get',
        url: {
          pathname: PATIENT_GET_ACTIVE_PROGRAM,
          params
        }
      })

      dispatch(setPatientActiveProgram(activeProgram))

      return true
    } catch (error) {
      catchError(error)
    }
  }

export const fetchPatientAllPrograms =
  (config: any) => async (dispatch: any) => {
    const { params = {}, query = {} } = config

    try {
      const { items = [] } = await httpClient.request({
        method: 'get',
        url: {
          pathname: PATIENT_GET_ALL_PROGRAMS,
          params,
          query
        }
      })

      dispatch(setPatientAllPrograms(items))

      return true
    } catch (error) {
      catchError(error)
    }
  }

export const fetchPatientVitalGraph =
  (config: any, type: string | number = 0) =>
  async (dispatch: any) => {
    const { params = {}, query = {} } = config

    try {
      const vitalGraph = await httpClient.request({
        method: 'get',
        url: {
          pathname: PATIENT_GET_VITAL_GRAPH,
          params,
          query
        }
      })

      dispatch(setPatientVitalGraph({ ...vitalGraph, type }))
    } catch (error: any) {
      catchError(error)
    }
  }

export const fetchPatientAssignedMeasurement =
  (config: any = {}) =>
  async (dispatch: any) => {
    const { params = {} } = config

    try {
      const items = await httpClient.request({
        method: 'get',
        url: {
          pathname: PATIENT_GET_ASSIGNED_MEASUREMENT,
          params
        }
      })

      dispatch(setPatientAssignedMeasurement(items))

      return items
    } catch (error) {
      catchError(error)
    }
  }

export const fetchPatientMeasurementActivityLog =
  (config: any, forModal = false) =>
  async (dispatch: any) => {
    const { params = {}, query = {} } = config

    try {
      const { items = [], meta = {} } = await httpClient.request({
        method: 'get',
        url: {
          pathname: PATIENT_GET_ACTIVITY_LOG,
          params,
          query
        }
      })

      if (forModal) {
        return items
      }

      dispatch(setPatientMeasurementActivityLog(items))

      return meta
    } catch (error: any) {
      catchError(error)

      return {}
    }
  }

export const fetchPatientActivityLog =
  (config: any) => async (dispatch: any) => {
    const { params = {}, query = {} } = config

    try {
      const { items = [], meta = {} } = await httpClient.request({
        method: 'get',
        url: {
          pathname: PATIENT_GET_ACTIVITY_LOG,
          params,
          query
        }
      })

      dispatch(setPatientActivityLog(items))

      return meta
    } catch (error: any) {
      catchError(error)

      return {}
    }
  }

export const exportPatientActivityLogReport =
  (config: any, type: any) => async () => {
    const updConfig = {
      pathname:
        type === 'xls'
          ? PATIENT_GET_ACTIVITY_LOG_EXPORT
          : PATIENT_GET_ACTIVITY_LOG_EXPORT_PDF,
      ...config
    }

    await exportReport(
      updConfig,
      `patient_activity_log_${dayjs().format(
        DATE_TIME_FORMAT_FOR_SERVER
      )}.${type}`
    )
  }

export const fetchPatientGoals = (config: any) => async (dispatch: any) => {
  const { params = {}, query = {} } = config

  try {
    const { items = [] } = await httpClient.request({
      method: 'get',
      url: {
        pathname: PATIENT_GET_GOALS,
        params,
        query
      }
    })

    dispatch(setPatienttGoals(items))
  } catch (error: any) {
    catchError(error)
  }
}

export const fetchPatientGoalInterventions =
  (config: any, patientGoalUuid: string) => async (dispatch: any) => {
    const { params = {}, query = {} } = config

    try {
      const { items = [] } = await httpClient.request({
        method: 'get',
        url: {
          pathname: PATIENT_GET_GOAL_INTERVENTIONS,
          params,
          query
        }
      })

      dispatch(setPatientGoalInterventions({ items, patientGoalUuid }))
    } catch (error: any) {
      catchError(error)
    }
  }

export const exportCarePlanReport =
  (config: any = {}) =>
  async () => {
    const updConfig = {
      pathname: PATIENT_GET_CARE_PLAN_REPORT,
      ...config
    }

    await exportReport(
      updConfig,
      `patient_care_plan_${dayjs().format(DATE_TIME_FORMAT_FOR_SERVER)}.pdf`
    )
  }

export const downloadPatientExternalDocument = async (config: any = {}) => {
  const { params = {} } = config

  try {
    const { data, headers } = await httpClient.request({
      method: 'get',
      url: {
        pathname: PATIENT_EXTERNAL_DOCUMENT_GET_PATCH_DELETE,
        params
      },
      isDownload: true
    })
    const mappedHeaders = {}
    Object.keys(headers).forEach((key) => {
      mappedHeaders[key.toLocaleLowerCase()] = headers[key]
    })
    const contentDisposition = get(mappedHeaders, 'content-disposition', '')
    const fileName = contentDisposition
      .slice(contentDisposition.indexOf('filename') + 9)
      .replaceAll('"', '')
    const url = window.URL.createObjectURL(new Blob([data]))
    const link = document.createElement('a')
    link.href = url

    link.setAttribute('download', fileName)

    document.body.appendChild(link)

    link.click()
  } catch (err) {
    catchError(err)
  }
}

export const deletePatientExternalDocument = async (config: any = {}) => {
  const { params } = config

  try {
    await httpClient.request({
      method: 'delete',
      url: {
        pathname: PATIENT_EXTERNAL_DOCUMENT_GET_PATCH_DELETE,
        params
      }
    })

    return true
  } catch (err) {
    catchError(err)

    return false
  }
}
